import moment from 'moment'
import React, { PureComponent, ReactNode } from 'react'
import { DispatchProp } from 'react-redux'
import { AnyAction } from 'redux'
import { Button, Modal, Spinner } from '../../../components'
import { calculateMinutesToArrive, getWeekDays } from '../../../helpers'
import { images } from '../../../images'
import { IExpeditionType, IServiceInfo, IStop, IStopAvailabilityTimes } from '../../../models'
import type { IWeekday } from '../../../models/weekday'
import { LiteralsService } from '../../../services'
import { connectRedux } from '../../../helpers/connectRedux'
import {
  fetchStopAvailabilityTimesAction,
  resetStopAvailabilityTimesAction
} from '../../../store/actions'
import { IStoreState } from '../../../store/states'
import StyledDiv from './styles'

interface IOwnProps {
  stop: IStop
  onClose: () => void
  // picking a departure also picks its stop, and the date the traveller chose here
  onSelectStop: (stop: IStop, date: Date) => void
}

interface IProps extends IOwnProps, DispatchProp<AnyAction> {
  serviceInfos: IServiceInfo[]
  stopAvailabilityTimes: IStopAvailabilityTimes[]
  isFetchingStopAvailabilityTimes: boolean
}

interface IState {
  pickedDay: IWeekday
}

class StopSchedulesModal extends PureComponent<IProps, IState> {
  private days: IWeekday[] = getWeekDays()

  constructor(props: IProps) {
    super(props)
    this.state = { pickedDay: this.days[0] }
  }

  public componentDidMount(): void {
    this.fetchTimes(this.state.pickedDay.date)
  }

  public componentWillUnmount(): void {
    this.props.dispatch(resetStopAvailabilityTimesAction())
  }

  public render(): ReactNode {
    const { stop, isFetchingStopAvailabilityTimes } = this.props
    const { pickedDay } = this.state

    return (
      <Modal isHidden={ false } onOverlayClick={ this.props.onClose }>
        <StyledDiv>
          <header>
            <div className="stop">
              <span className="label">{ LiteralsService.get('schedules', true) }</span>
              <span className="name">{ stop.name }</span>
            </div>
            <button className="close" onClick={ this.props.onClose } aria-label={ LiteralsService.get('close', true) }>
              <img src={ images.close } alt="close" />
            </button>
          </header>

          <span className="days-question">{ LiteralsService.get('whichDayDoYouWantToGo', true) }</span>
          <div className="days">
            { this.days.map(day => (
              <div
                key={ day.index }
                className={ `day ${ moment(day.date).isSame(pickedDay.date, 'day') ? 'selected' : '' } ${ isFetchingStopAvailabilityTimes ? 'disabled' : '' }` }
                onClick={ () => this.handlePickDay(day) }
              >
                <span className="weekday">{ day.weekday }</span>
                <span>{ `${ day.day } ${ day.month }` }</span>
              </div>
            )) }
          </div>

          { isFetchingStopAvailabilityTimes ? <Spinner /> : this.availabilities }

          <footer>
            <Button
              title={ LiteralsService.get('selectStop') }
              styles={{ width: '100%' }}
              onPress={ () => this.handleSelectStop(pickedDay.date) }
              upper
              filled
            />
          </footer>
        </StyledDiv>
      </Modal>
    )
  }

  private get availabilities(): ReactNode {
    const { stopAvailabilityTimes } = this.props

    if (!stopAvailabilityTimes.length) {
      return (
        <div className="empty">
          <span className="title">{ LiteralsService.get('noAvailabilities.title', true) }</span>
          <span className="description">{ LiteralsService.get('noAvailabilities.description', true) }</span>
        </div>
      )
    }

    return (
      <div className="availabilities">
        { stopAvailabilityTimes.map((availability, index) => (
          <div
            className="availability"
            key={ `stop-availability-${ index }` }
            onClick={ () => this.handleSelectStop(new Date(availability.expectedDateTime)) }
          >
            <div className="time">
              <img src={ this.timeIcon(availability) } alt="time-icon" />
              <span>{ this.timeLabel(availability) }</span>
            </div>
            <div className="destination">
              { this.tag(availability.serviceInfo) }
              <span>{ availability.destination }</span>
            </div>
            <span className="state">{ this.stateLabel(availability) }</span>
          </div>
        )) }
      </div>
    )
  }

  private tag(serviceInfo: IServiceInfo): ReactNode {
    if (!serviceInfo?.tag || serviceInfo.tag.trim() === '' || serviceInfo.tag.trim() === '-') return null

    // A white service colour would leave the tag invisible, so fall back to a light grey.
    const backgroundColor = serviceInfo.color === '#FFFFFFFF' ? '#00000020' : `${ serviceInfo.color }80`

    return <span className="tag" style={{ backgroundColor }}>{ serviceInfo.tag.trim() }</span>
  }

  // 'Ongoing' means the vehicle is already under way: the time is a live estimate.
  private isRealtime(availability: IStopAvailabilityTimes): boolean {
    return availability.status === 'Ongoing'
  }

  // Only services with an actual timetable show a departure time; a dynamic one
  // can just offer the window in which it operates.
  private hasFixedTime(availability: IStopAvailabilityTimes): boolean {
    return !!availability.expedition ||
      !!availability.serviceInfo?.informative ||
      availability.serviceAvailability?.expeditionType === IExpeditionType.Fixed
  }

  private timeIcon(availability: IStopAvailabilityTimes): string {
    if (!this.hasFixedTime(availability)) return images.avgPace
    return this.isRealtime(availability) ? images.realTime : images.clock
  }

  private timeLabel(availability: IStopAvailabilityTimes): string {
    if (!this.hasFixedTime(availability)) {
      const start = moment(availability.serviceAvailability?.startDateTime).format('HH:mm')
      const end = moment(availability.serviceAvailability?.endDateTime).format('HH:mm')
      return start === end ? start : `${ start } - ${ end }`
    }

    if (this.isRealtime(availability)) {
      return LiteralsService.get('inMinutes').replace('{0}', String(calculateMinutesToArrive(availability.expectedDateTime)))
    }

    return moment(availability.expectedDateTime).format('HH:mm')
  }

  private stateLabel(availability: IStopAvailabilityTimes): string {
    if (availability.serviceInfo?.informative || availability.serviceAvailability?.expeditionType === IExpeditionType.Fixed) {
      return LiteralsService.get('informative', true)
    }
    if (this.isRealtime(availability)) return LiteralsService.get('en-route', true)
    if (availability.expedition) return LiteralsService.get('scheduled', true)

    return LiteralsService.get('on-demand', true)
  }

  private handleSelectStop = (date: Date): void => {
    const { stop, onSelectStop, onClose } = this.props

    onSelectStop(stop, date)
    onClose()
  }

  private handlePickDay = (day: IWeekday): void => {
    if (this.props.isFetchingStopAvailabilityTimes) return

    this.setState({ pickedDay: day })
    this.fetchTimes(day.date)
  }

  private fetchTimes(date: Date): void {
    const { dispatch, stop, serviceInfos } = this.props
    const selected = serviceInfos.filter(serviceInfo => serviceInfo.selected)

    dispatch(fetchStopAvailabilityTimesAction(stop.id, date, selected.length ? selected : serviceInfos))
  }
}

const mapStateToProps = ({ newReservation }: IStoreState) => ({
  serviceInfos: newReservation.serviceInfos,
  stopAvailabilityTimes: newReservation.stopAvailabilityTimes,
  isFetchingStopAvailabilityTimes: newReservation.isFetchingStopAvailabilityTimes
})

export default connectRedux(mapStateToProps, StopSchedulesModal as any) as React.ComponentType<IOwnProps>
